import { YES_NO } from 'components/v2/compiler/constants';
import LcaResultsGridAdditionalConstruction from 'components/v2/compiler/grids/lca-results/LcaResultsGridAdditionalConstruction';
import LcaResultsGridAdditionalMandatory from 'components/v2/compiler/grids/lca-results/LcaResultsGridAdditionalMandatory';
import LcaResultsGridAdditionalVoluntary from 'components/v2/compiler/grids/lca-results/LcaResultsGridAdditionalVoluntary';
import LcaResultsGridCoreConstruction from 'components/v2/compiler/grids/lca-results/LcaResultsGridCoreConstruction';
import LcaResultsGridOutputFlowConstruction from 'components/v2/compiler/grids/lca-results/LcaResultsGridOutputFlowConstruction';
import LcaResultsGridResourceConstruction from 'components/v2/compiler/grids/lca-results/LcaResultsGridResourceConstruction';
import LcaResultsGridWasteConstruction from 'components/v2/compiler/grids/lca-results/LcaResultsGridWasteConstruction';
import React from 'react';
import { INDICATOR_GROUP_CODES } from 'services/api/constants';
import DebugOnly from 'util/dev-tools/DebugOnly';
import RenderCounter from 'util/dev-tools/RenderCounter';

import { PdfAreaDontBreak, PdfGridsArea, PdfH1, PdfH3, PdfP, PdfPage } from './Styles.styled';
import { LcaResultModel, LCASpecificationModel } from 'types/types';

const LCAResultsPage: React.FunctionComponent<{
  epdVersionId: string;
  infrastructureAndCapitalGoods: any;
  lcaResults: LcaResultModel[] | undefined;
  additionalLcaResults: LcaResultModel[] | undefined;
  lcaSpecification?: LCASpecificationModel | null | undefined;
}> = ({ epdVersionId, infrastructureAndCapitalGoods, lcaResults, additionalLcaResults, lcaSpecification }) => {
  return (
    <PdfPage className="landscape">
      <DebugOnly>
        <RenderCounter componentName="LCAResultsPage" threshold={3} />
      </DebugOnly>
      <PdfGridsArea>
        <PdfAreaDontBreak>
          <PdfH1>Environmental performance</PdfH1>
          <PdfP>
            The estimated impact results are only relative statements, which do not indicate the endpoints of the impact
            categories, exceeding threshold values, safety margins and/or risks.
          </PdfP>
          <PdfH3>Mandatory environmental performance indicators according to EN 15804</PdfH3>
          <LcaResultsGridCoreConstruction
            epdVersionId={epdVersionId}
            rows={lcaResults?.filter((x: any) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.CORE15804)}
            isAdditionalDisclaimerVisible={infrastructureAndCapitalGoods?.includedInProcesses === YES_NO.YES}
            isForPdf
          />
        </PdfAreaDontBreak>
        <PdfAreaDontBreak>
          <PdfH3>Additional mandatory environmental performance indicators</PdfH3>
          <LcaResultsGridAdditionalMandatory
            epdVersionId={epdVersionId}
            rows={lcaResults?.filter((x: any) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.ADDITIONALMANDATORY)}
            isForPdf
          />
        </PdfAreaDontBreak>
        <PdfAreaDontBreak>
          <LcaResultsGridAdditionalConstruction
            epdVersionId={epdVersionId}
            rows={lcaResults?.filter((x: any) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.ADDITIONALVOLUNTARY15804)}
            isAdditionalDisclaimerVisible={infrastructureAndCapitalGoods?.includedInProcesses === YES_NO.YES}
            isForPdf
          />
        </PdfAreaDontBreak>
        <PdfAreaDontBreak>
          <LcaResultsGridAdditionalVoluntary
            epdVersionId={epdVersionId}
            justificationForInclusion={lcaSpecification?.justificationForInclusion}
            isForPdf
          />
        </PdfAreaDontBreak>
        <PdfAreaDontBreak>
          <PdfH3>Resource use indicators according to EN 15804</PdfH3>
          <LcaResultsGridResourceConstruction
            epdVersionId={epdVersionId}
            rows={lcaResults?.filter((x: any) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.RESOURCE15804)}
            isForPdf
          />
        </PdfAreaDontBreak>
        <PdfAreaDontBreak>
          <PdfH3>Waste indicators according to EN 15804</PdfH3>
          <LcaResultsGridWasteConstruction
            epdVersionId={epdVersionId}
            rows={lcaResults?.filter((x: any) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.WASTE15804)}
            isForPdf
          />
        </PdfAreaDontBreak>
        <PdfAreaDontBreak>
          <PdfH3>Output flow indicators according to EN 15804</PdfH3>
          <LcaResultsGridOutputFlowConstruction
            epdVersionId={epdVersionId}
            rows={lcaResults?.filter((x: any) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.OUTPUTFLOW15804)}
            isForPdf
          />
        </PdfAreaDontBreak>
      </PdfGridsArea>
    </PdfPage>
  );
};

export default LCAResultsPage;
